import { Direction } from '@angular/cdk/bidi';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { LogService } from 'src/app/@shared/services/log/log.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { UserLocationService } from 'src/app/@shared/services/user-location/user-location.service';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import {
  LOCATION_PERMISSION_REQUIRED,
  START_ROUTE,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eSectionType } from 'src/app/modules/route-planner/enums/route-planning/section-type.enum';
import { RoutePlanning } from 'src/app/modules/route-planner/interfaces/route-planning/@route-planning.interface';
import { SourceDestinationForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/source-destination-form';
import { IntermediateStop } from 'src/app/modules/route-planner/interfaces/route-planning/intermediate-stop.interface';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';

import { MapService } from 'src/app/modules/route-planner/services/map/map.service';
import { Section } from 'src/app/modules/route-planner/interfaces/route-planning/section.interface';
import { MAP_ICONS } from 'src/app/modules/route-planner/constants/map-icons.constants';
import { Address } from 'src/app/modules/route-planner/interfaces/location/address.interface';
import { eTransportMode } from 'src/app/modules/route-planner/enums/route-planning/transport-mode.enum';
import { Coordinate } from 'src/app/modules/route-planner/interfaces/location/coordinate.interface';

@Component({
  selector: 'app-source-destination-detailed-result-route',
  templateUrl: './source-destination-detailed-result-route.component.html',
  styleUrls: ['./source-destination-detailed-result-route.component.scss'],
  standalone: false
})
export class SourceDestinationDetailedResultRouteComponent
  implements OnInit, OnDestroy {
  @Input() formValues: SourceDestinationForm;
  @Input() route: RoutePlanning;
  @Input() leg: Address | null;

  sectionTypes = eSectionType;

  direction: Direction;
  startRouteText: string;
  requiredLocationPermissionsText: string;

  hasLocationPermissions: boolean = this.userLocationService.hasPermissions;

  private _focusMarkers: H.map.Object[] = [];

  constructor(
    public readonly routePlanningService: RoutePlanningService,
    public readonly utilsService: UtilsService,
    public readonly userLocationService: UserLocationService,
    public readonly resizeService: ResizeService,
    private readonly languageService: LanguageService,
    private readonly mapService: MapService
  ) { }
  ngOnDestroy(): void {
    // Clear any focus markers that might still be on the map
    this.clearFocusMarkers();

    // Clear the map
    this.mapService.clearMap();

    // Stop tracking when component is destroyed
    this.routePlanningService.stopTrackingRoute();
  }
  ngOnInit(): void {
    LogService.logInfo('Selected route', this.route);
    this.initializeMultiLanguageTexts();
  }

  _cast(section: any): IntermediateStop {
    return section as IntermediateStop;
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.direction = language.direction;
      this.startRouteText = START_ROUTE[language.language];
      this.requiredLocationPermissionsText =
        LOCATION_PERMISSION_REQUIRED[language.language];
    });
  }

  startTrackingRoute() {
    if (!this.hasLocationPermissions) {
      console.warn('[ROUTE_TRACKING] Location permissions not granted');
      return;
    }

    this.routePlanningService.startTrackingRoute(this.route);
  }

  /**
   * When the user taps on a route segment row (from the detailed results),
   * use its start and end locations to update the map view.
   *
   * (Make sure the row in your HTML calls `focusSegment(segment)` with the proper segment.)
   */
  focusSegment(segment: Section): void {
    const startLocation = segment.departure?.place?.location;
    const endLocation = segment.arrival?.place?.location;

    // Get the map service from the route planning service instead
    // This ensures we're using the same instance that has the initialized map
    const mapService = this.routePlanningService.getMapService();

    if (startLocation && endLocation && mapService && mapService.map) {
      // Remove any previous focus markers
      this.clearFocusMarkers();

      // Add new markers for the segment
      const departureMarker = mapService.addMarker(
        startLocation,
        MAP_ICONS.pin_green()
      );
      const arrivalMarker = mapService.addMarker(
        endLocation,
        MAP_ICONS.pin_green()
      );

      // Store the markers for later removal
      this._focusMarkers.push(departureMarker, arrivalMarker);

      // Focus the map on the segment
      mapService.lookAtData([startLocation, endLocation], true);
    } else {
      console.warn(
        'Cannot focus segment: Map not initialized or locations missing'
      );
    }
  }

  focusLocation(location: Coordinate) {
    if (location?.lat && location?.lng) {
      const mapService = this.routePlanningService.getMapService();
      mapService.updateMapView(location.lat, location.lng, 16)
    }
  }

  /**
   * Helper method to clear all focus markers from the map
   */
  private clearFocusMarkers(): void {
    if (this._focusMarkers.length > 0) {
      const mapService = this.routePlanningService.getMapService();
      if (mapService && mapService.map) {
        this._focusMarkers.forEach((marker) => {
          mapService.removeObject(marker);
        });
      }
      this._focusMarkers = [];
    }
  }

  isNextSectionCarAfterWalk(section: Section, index: number) {
    if (
      this.leg &&
      index + 1 < this.route.sections.length &&
      section.transport.mode === eTransportMode.PEDESTRIAN &&
      this.route.sections[index + 1].transport.mode === eTransportMode.CAR
    ) {
      return true;
    }

    return false;
  }

  isWalkingToStop(section: Section, index: number) {
    // Only scenario when you have 2 walking sections one after another is if a stop involved
    if (index < this.route.sections.length - 1 && section.transport.mode === eTransportMode.PEDESTRIAN && this.route.sections[index + 1].transport.mode === eTransportMode.PEDESTRIAN) {
      return true
    }
    return false
  }

  getSectionColor(sectionType: string): string {
    return sectionType === eSectionType.PEDESTRIAN ? '#AD76F9' : '#0068F5';
  }

  isWalkedToStop(section: Section, index: number) {
    if (index === 0) {
      return false
    }
    if (index < this.route.sections.length - 1 && section.transport.mode !== eTransportMode.PEDESTRIAN && section.transport.mode !== eTransportMode.CAR && this.route.sections[index - 1].transport.mode === eTransportMode.PEDESTRIAN) {
      return true
    }
    return false
  }
}
